import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, CircularProgress, Grid, makeStyles, Paper } from "@material-ui/core";
import { Autocomplete } from "@totalsoft/rocket-webapp-components-deprecated";
import { useTranslation } from "react-i18next";
import { Add } from "@material-ui/icons";
import { get, set } from "@totalsoft/change-tracking-react";
import { emptyArray, emptyString } from "constants/defaultValues";
import { useClientQueryWithErrorHandling, useQueryWithErrorHandling } from "hooks/errorHandling";
import { ASSETS_QUERY, EXCHANGE_RATE_QUERY } from "../queries";
import assetPageStyle from "features/assets/styles";
import SectionTitle from "features/common/components/SectionTitle";
import useDebounce from "hooks/useDebounce";
import { useStateLens } from "@totalsoft/react-state-lens";
import useUserData from "hooks/useUserData";
import { CURRENCIES_QUERY } from "features/common/queries";
import { eurCurrency } from "constants/currency";
import { useParams } from "react-router";

const useStyles = makeStyles(assetPageStyle);

function SearchAsset(props) {
  const { disabled, saving, onAdd, quoteLens } = props;
  const quote = quoteLens |> get;
  const { quoteId } = useParams();
  const quoteExists = Boolean(quoteId);
  const { t } = useTranslation();
  const classes = useStyles();
  const clientQuery = useClientQueryWithErrorHandling();
  const { isExchangeRateFunctionality } = useUserData();
  const [searchAssetInput, setSearchAssetInput] = useState();
  const searchAssetInputDebounced = useDebounce(searchAssetInput, 500);
  const selectedAssetLens = useStateLens();

  const { data: currenciesData } = useQueryWithErrorHandling(CURRENCIES_QUERY);
  const currencies = currenciesData?.currencies || emptyArray;

  const updateQuote = useCallback(
    async (currency) => {
      const quote = quoteLens |> get;
      if (currency.id === quote.currencyId) return;
      if (currency.code !== eurCurrency.code && isExchangeRateFunctionality) {
        const { data } = await clientQuery(EXCHANGE_RATE_QUERY, {
          notifyOnNetworkStatusChange: true,
          variables: { fromCurrencyCode: currency.code, toCurrencyCode: eurCurrency.code },
        });
        (quoteLens |> set)({
          ...quote,
          currencyId: currency.id,
          currencyCode: currency.code,
          exchangeRate: data?.exchangeRate?.exchangeRate,
          exchangeRateDate: data?.exchangeRate?.exchangeRateDate,
        });
      } else {
        (quoteLens |> set)({
          ...quote,
          currencyId: currency.id,
          currencyCode: currency.code,
          exchangeRate: null,
          exchangeRateDate: null,
        });
      }
    },
    [quoteLens, isExchangeRateFunctionality, clientQuery]
  );

  const handleCurrencyChange = useCallback(
    (currencyId) => {
      const currency = currencies.find((c) => c.id === currencyId);
      updateQuote(currency);
    },
    [currencies, updateQuote]
  );

  const loadAssets = useCallback(async () => {
    if (!searchAssetInputDebounced || searchAssetInputDebounced?.length < 2 || !quote?.currencyId) {
      return selectedAssetLens |> get ? [selectedAssetLens |> get] : emptyArray;
    }
    const { data } = await clientQuery(ASSETS_QUERY, {
      variables: { keyword: searchAssetInputDebounced ?? emptyString, currencyId: quote.currencyId },
    });
    return data.assets;
  }, [clientQuery, searchAssetInputDebounced, quote?.currencyId, selectedAssetLens]);

  const handleChangeSearchAsset = useCallback(
    (input) => {
      setSearchAssetInput(input?.target?.value);
    },
    [setSearchAssetInput]
  );

  const handleAdd = useCallback(() => {
    onAdd(selectedAssetLens |> get);
    (selectedAssetLens |> set)();
  }, [selectedAssetLens, onAdd]);

  const handleClear = useCallback(() => {
    setSearchAssetInput();
  }, []);

  useEffect(() => {
    if (!currencies?.length || quoteExists || quote.currencyId) return;
    updateQuote(currencies[0]);
  }, [currencies, quoteExists, quote?.currencyId, updateQuote]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SectionTitle title={t("Asset.AddAssets")} subtitle={t("Asset.PleaseAddAssets")} />
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.filterStyle} style={{ padding: "15px" }}>
          <Grid container spacing={1} direction="row" justifyContent="space-between" alignItems="flex-end">
            <Grid item xs={12} sm={2}>
              <Autocomplete
                label={t("Asset.Currency")}
                labelKey="code"
                valueKey="id"
                simpleValue
                options={currencies}
                onChange={handleCurrencyChange}
                value={quote.currencyId}
                disabled={Boolean(quote?.quoteAssets?.length)}
                noOptionsText={t("General.AutocompleteNoOptions")}
              />
            </Grid>
            <Grid item xs={12} sm={9}>
              <Autocomplete
                label={t("Asset.AssetsSearch")}
                labelKey="assetDisplayName"
                valueKey="assetId"
                loadOptions={loadAssets}
                onInputChange={handleChangeSearchAsset}
                defaultOptions
                isClearable={true}
                onChange={selectedAssetLens |> set}
                value={selectedAssetLens |> get}
                disabled={disabled}
                noOptionsText={t("Asset.AssetsSearchNoOptions")}
                onClose={handleClear}
              />
            </Grid>
            <Grid item xs={12} sm={1} container justifyContent="flex-end">
              <Button
                id="assetSearch_SearchBtn"
                right="true"
                variant="contained"
                color="primary"
                onClick={handleAdd}
                endIcon={saving ? <CircularProgress size={20} color={"inherit"} /> : <Add />}
                disabled={disabled || !(selectedAssetLens |> get)}
              >
                {t("General.Add")}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}

SearchAsset.propTypes = {
  quoteLens: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  onAdd: PropTypes.func.isRequired,
};

export default SearchAsset;
